import { useScrollReveal } from '@/hooks/useScrollReveal';

interface LocationSectionProps {
  mapEmbed: string;
  locationName: string;
}

const locationAdvantages = [
  {
    icon: 'ri-map-pin-2-line',
    title: 'Prime Islamabad Location',
    desc: 'Situated in the fast-developing E-19 sector with direct access to major city arteries.',
  },
  {
    icon: 'ri-road-map-line',
    title: 'Motorway Connectivity',
    desc: 'Minutes away from M-1 & M-2 Motorway interchange for seamless travel nationwide.',
  },
  {
    icon: 'ri-school-line',
    title: 'Nearby Educational Hub',
    desc: 'Access to top schools, colleges, and universities within a 10-minute drive.',
  },
  {
    icon: 'ri-hospital-line',
    title: 'Healthcare Access',
    desc: 'Major hospitals and medical centers located within close proximity.',
  },
  {
    icon: 'ri-shopping-bag-3-line',
    title: 'Commercial Zones',
    desc: 'Upcoming commercial districts and shopping centers in the immediate vicinity.',
  },
  {
    icon: 'ri-plane-line',
    title: 'Airport Proximity',
    desc: 'Islamabad International Airport accessible within 30 minutes via expressway.',
  },
];

export default function LocationSection({ mapEmbed, locationName }: LocationSectionProps) {
  const { ref, isVisible } = useScrollReveal();

  return (
    <section ref={ref} className="w-full py-16 md:py-24 bg-[#FAFAF8]">
      <div className="max-w-7xl mx-auto px-5 md:px-10 lg:px-16">
        <div className={`text-center mb-12 md:mb-16 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <span className="inline-block text-[11px] font-bold text-[#d4853a] uppercase tracking-[0.2em] mb-3">
            Strategic Location
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-[2.25rem] font-extrabold text-charcoal-300 leading-tight mb-4">
            Location <span className="text-[#d4853a]">Advantages</span>
          </h2>
          <p className="text-[15px] text-charcoal-50 max-w-xl mx-auto leading-relaxed">
            Positioned at the intersection of growth and convenience in Islamabad&apos;s emerging real estate corridor.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_1fr] gap-8 lg:gap-12">
          {/* Map */}
          <div
            className={`relative rounded-2xl overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.08)] border border-ivory-200 transition-all duration-700 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
          >
            {mapEmbed ? (
              <div className="aspect-[4/3] md:aspect-[16/11]">
                <iframe
                  src={mapEmbed}
                  title={`${locationName} Location Map`}
                  className="w-full h-full"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            ) : (
              <div className="aspect-[4/3] md:aspect-[16/11] bg-ivory-100 flex items-center justify-center">
                <div className="text-center">
                  <i className="ri-map-pin-line text-4xl text-ivory-300 mb-3" />
                  <p className="text-sm text-charcoal-50">Interactive map coming soon</p>
                  <p className="text-xs text-charcoal-50 mt-1">{locationName}</p>
                </div>
              </div>
            )}
            {/* Location label overlay */}
            <div className="absolute bottom-4 left-4 px-4 py-2 bg-white/90 backdrop-blur-md rounded-xl shadow-md border border-ivory-200">
              <div className="flex items-center gap-2">
                <i className="ri-map-pin-2-line text-[#d4853a] text-sm" />
                <span className="text-sm font-semibold text-charcoal-300">{locationName}</span>
              </div>
            </div>
          </div>

          {/* Advantages list */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4 md:gap-5">
            {locationAdvantages.map((item, i) => (
              <div
                key={item.title}
                className={`flex items-start gap-4 p-4 md:p-5 bg-white rounded-xl border border-ivory-200 transition-all duration-500 hover:shadow-md hover:-translate-y-0.5 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ transitionDelay: isVisible ? `${300 + i * 80}ms` : '0ms' }}
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#d4853a]/8 text-[#d4853a] shrink-0">
                  <i className={`${item.icon} text-lg`} />
                </div>
                <div>
                  <h3 className="text-[14px] font-bold text-charcoal-300 mb-0.5">{item.title}</h3>
                  <p className="text-[12px] text-charcoal-50 leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}